import React from 'react';
import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import { AccessTime, OpenInNew } from '@mui/icons-material';

import CopyButton from '../../../../components/core/CopyButton';
import type { ShopifyOnboardingStep } from '../../../../store/modules/merchant/types';
import { StatusChip } from '../../../ShopifyProvisioning/StatusChip';
import { getStatusLabel, ONBOARDING_STATUS_COLORS } from '../../../ShopifyProvisioning/utils';

type StepCardProps = {
  number: number;
  title: string;
  status: ShopifyOnboardingStep['status'];
  /** Overrides the generic label for the status, e.g. "Expired" instead of "Failed". */
  statusLabel?: string;
  children: React.ReactNode;
};

export const StepCard = ({
  number,
  title,
  status,
  statusLabel,
  children,
}: StepCardProps): JSX.Element => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" gap={2}>
        <Typography component="h2" fontWeight="semibold">
          {number}. {title}
        </Typography>
        <Box sx={{ flex: 1, borderBottom: 1, borderColor: 'divider' }} />
        <StatusChip
          label={statusLabel ?? getStatusLabel(status)}
          color={ONBOARDING_STATUS_COLORS[status]}
        />
      </Stack>
      <Stack spacing={2}>{children}</Stack>
    </Stack>
  </Paper>
);

const actionUrlSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  minWidth: 0,
  fontFamily: 'Poppins, sans-serif',
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.4,
  color: 'link.main',
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  wordBreak: 'break-all',
} as const;

export const ActionUrl = ({ url, copyTitle }: { url: string; copyTitle: string }): JSX.Element => (
  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
    <Link href={url} target="_blank" rel="noreferrer" sx={actionUrlSx}>
      {url}
      <OpenInNew sx={{ fontSize: 16, flexShrink: 0 }} aria-hidden />
    </Link>
    <CopyButton copyString={url} title={copyTitle} />
  </Stack>
);

export const WaitingHint = ({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: React.ReactNode;
}): JSX.Element => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" data-testid={testId}>
    <AccessTime sx={{ mt: '2px', fontSize: 20, color: 'text.secondary' }} aria-hidden />
    <Box>
      <Typography variant="body2" fontWeight="semibold">
        {title}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.5, lineHeight: 1.5 }}
      >
        {children}
      </Typography>
    </Box>
  </Stack>
);

const refreshLinkSx = {
  p: 0,
  border: 0,
  background: 'none',
  font: 'inherit',
  fontWeight: 600,
  color: 'link.main',
  verticalAlign: 'baseline',
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
} as const;

export const StatusRefreshLink = ({
  label,
  onCheckStatus,
  isRefreshing,
}: {
  label: string;
  onCheckStatus: () => void;
  isRefreshing: boolean;
}): JSX.Element => (
  <Link
    component="button"
    type="button"
    variant="body2"
    onClick={onCheckStatus}
    disabled={isRefreshing}
    sx={{ ...refreshLinkSx, cursor: isRefreshing ? 'default' : 'pointer' }}
  >
    {label}
  </Link>
);
